// NOTES
//-------------------------------------------------------------------------------------------------------------------------
// Component: cas-grid
// Created: 6/2017
// Author: Adam Smith
// This is a simple grid for angular 4 that is intended to include native 508 support.  It was originally created for the CPDES project.


// Example HTML usage:
/*
    <cas-grid #practicesGrid
                [settings]="_gridSettings" 
                summary="Something List"                        invisible summary for jaws reading and 508
                (onRefreshClicked)="gridRefreshClicked()"
                (onRowSelected)="gridRowSelected($event)">
    </cas-grid>
*/

// Example JS usage:
/*
    //Reference to the grid if you want to call its public functions directly in javascript
    @ViewChild('practicesGrid') practicesGrid; 

    //Examine the property below: _default_settings
    //It has notes indicating what properties are available and what they do.
    //The grid will also have default columns and data for testing if you provide none

*/

//Layout Notes
/*
    If you want the grid to consume the size of the screen or an available space so that the scroll bar doesn't affect
    the entire browser page, put the grid inside an element of the desired dimensions (having a position of absolute
    or relative).  Then set the grid's positionAbsolute = true.  The grid will position itself as absolute with each
    edge set to 0.

    I recommend using "display:flex" to get your containing elements sized the way you like (i.e. can easily up empty space
    without trying to size based on content).
*/


// IMPORTS
//-------------------------------------------------------------------------------------------------------------------------
//Angular
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';


//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// CASGridComponent
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
@Component({
  selector : 'cas-grid',
  styles : ['.__cas-grid{display:flex;flex-direction:column;border-bottom:solid 1px #ebebe4;font-size:12px;overflow-x:hidden;overflow-y:hidden}.__cas-grid-disabled{background-color:#ebebe4}.__cas-grid-block{width:100%;height:100%}.__cas-grid-absolute{position:absolute;top:27px;right:0px;bottom:0px;left:0px}.__cas-grid div{outline:none}.__cas-grid-control-row{display:flex;padding:2px 4px;align-content:center;border-top:solid 0 #ccc;border-bottom:solid 1px #ccc;background-color:#fff;height:26px}.__cas-grid-controls{flex-grow:1;display:flex;align-items:center}.__cas-grid-pagination{display:flex;align-items:center}.__cas-grid-pagination-icons{font-size:16px;cursor:pointer;color:#205493;border:solid 2px transparent}.__cas-grid-pagination-icons:hover{background-color:#205493;color:#fff;border-radius:4px}.__cas-grid-pagination-icons:focus{border:solid 2px #009AFA}.__cas-grid-pagination-input{line-height:1em;width:35px;margin:0 8px 0 8px}.__cas-grid-rows-per-page-div{display:flex;align-items:center;margin-left:5px}.__cas-grid-rows-per-page-input{line-height:1em;width:35px;margin-left:5px}.__cas_grid_search_box{align-items:center;display:flex}.__cas-grid-col-header-row{flex-shrink:0;display:flex;font-size:12px;font-weight:bold;border-top:solid 0 #b8b8b8;z-index:2;background-color:#f0f0f0;box-shadow:0 1px 5px 0 rgba(0,0,0,0.2)}.__cas-grid-col-header-cell{display:flex;padding:2px 4px;overflow:hidden;border:solid 2px transparent}.__cas-grid-col-header-cell:focus{border:solid 2px #009AFA}.__cas-grid-col-header-cell-sortable{cursor:pointer}.__cas-grid-col-header-cell-sortable:hover{text-decoration:underline}.__cas-grid-col-header-cell-sort-icons{font-size:12px;cursor:pointer;color:#205493}.__cas-grid-body{flex-grow:1;overflow-y:scroll;overflow-x:hidden;background-color:#fff;z-index:1;border-bottom:solid 1px #b8b8b8}.__cas-grid-body-disabled{background-color:#f0f0f0}.__cas-grid-row{display:flex;align-items:center;cursor:pointer;border-bottom:solid 1px #b8b8b8}.__cas-grid-row:nth-child(even){background-color:#fff;border-bottom:solid 1px #b8b8b8}.__cas-grid-row:hover{background-color:#f0f0f0}.__cas-grid-row:focus{border:solid 2px #009AFA}.__cas-grid-row-selected{background-color:#d4e2f2 !important}.__cas-grid-row-disabled{display:flex;cursor:default;border:solid 2px transparent;background-color:#CCC;font-style:italic}.__cas-grid-row-disabled:focus{border:solid 2px #009AFA}.__cas-grid-cell{display:flex;align-items:center;padding:2px 4px;overflow-x:hidden;border:solid 2px transparent}.__cas-grid-cell:focus{border:solid 2px #009AFA}.__cas-grid-cell-editable-div{display:flex;flex-grow:1;border:solid 1px #999;border-radius:2px}.__cas-grid-cell-input{width:100%;margin:0 0}.__cas-grid-cell-inputmarker-span{width:100%;margin:0 0;margin:0 4px 0 4px}.__cas-grid-chkbox-cell{border:solid 2px transparent}.__cas-grid-icon{font-size:16px;cursor:pointer}.__cas-grid-icon:focus{border:solid 2px #009AFA}.__cas-grid-control-button{display:flex;align-items:center;border:solid 2px transparent;padding:0 2px;margin-right:4px;color:#205493;font-style:italic;cursor:pointer;border-radius:3px}.__cas-grid-control-button:focus{border:solid 2px #009afa;border-radius:3px}.__cas-grid-control-button:hover{background-color:#f0f0f0;color:#205493;border-radius:3px}.__cas-grid-checkdiv{font-size:14px}.__cas-grid-checkdiv-unchecked{color:#b8b8b8}.__cas-grid-checkdiv-checked{color:#205493;font-weight:bold}.__cas-grid-input-group-left{border-radius:3px 0 0 3px}.__cas-grid-input-group-right{border-radius:0 3px 3px 0}'],
  template : '<!--508 alerts-->\n<!--<span id="assertive508Alert" class="off-screen" role="alert" aria-live="assertive"></span>\n<span id="polite508Alert" class="off-screen" role="alert" aria-live="polite"></span>-->\n\n<span id="__cas-grid_accAlertAssertive" class="off-screen" role="alert" aria-live="assertive"></span>\n<span id="__cas-grid_accAlertPolite" class="off-screen" role="alert" aria-live="polite"></span>\n<span id="__cas-grid_accLiveAssertive" class="off-screen" aria-live="assertive"></span>\n<span id="__cas-grid_accLivePolite" class="off-screen" aria-live="polite"></span>\n\n<!--Grid navigation directions for Jaws users-->\n<div tabindex="0" style="border-bottom:solid 1px #ccc; margin:0; padding:0; height:0; width:0; overflow:hidden;">\n    Grid Navigation Instructions\n    ,. Use the arrow keys to navigate between rows and columns, excluding the column headers which must be navigated with the tab key\n    ,. Cells can also be navigated left and right with the tab key\n    ,. Press enter on an editable cell to begin editing\n    ,. While editing press the tab key to move to the next cell and continue editing\n    ,. Press enter on an editable cell while editing to stop editing\n</div>\n\n<div id="{{_gridId}}"\n     (mouseup)="_grid_mouseup($event)"\n     (keydown)="_grid_keydown($event)"\n     (keyup)="_grid_keyup($event)">\n\n    <!--Control Row-->\n    <div *ngIf="!settings.hideControlRow" class="__cas-grid-control-row {{settings.cssClasses_ControlRow}}" aria-label="Grid controls">\n\n        <!--Controls/Buttons-->\n        <div class="__cas-grid-controls {{settings.cssClasses_Controls}}">\n\n            <!--Refresh "Button"-->\n            <div *ngIf="!settings.hideRefreshButton" tabindex="0" aria-label="Refresh" role="button"\n                 id="__cas_grid_control_button_refresh"\n                 (click)="_refreshClicked($event)" (keydown)="_keydown_refreshButton($event)"\n                 class="__cas-grid-control-button {{settings.cssClasses_ControlButton}}"\n                 accesskey="r">\n\n                <div id="__cas_grid_control_refresh" class="mdi mdi-refresh __cas-grid-icon {{settings.cssClasses_GridIcon}}"></div>\n                <span style="margin-left:3px;"><u>R</u>efresh</span>\n                <!--<span class="off-screen">Refreshes Table</span>-->\n            </div>\n\n            <!--Clear Selected Rows "Button"-->\n            <div *ngIf="!settings.hideClearSelectedButton" tabindex="0" aria-label="Clear selected rows" role="button"\n                 id="__cas_grid_control_button_clear"\n                 (click)="_clearRowsClicked()" (keydown)="_keydown_clearRowsButton($event)"\n                 class="__cas-grid-control-button {{settings.cssClasses_ControlButton}}">\n\n                <div id="__cas_grid_control_clear_rows" class="mdi mdi-playlist-remove __cas-grid-icon {{settings.cssClasses_GridIcon}}"></div>\n                <span style="margin-left:3px;">Clear&nbsp;Selected</span>\n                <!--<span class="off-screen">Clears Selected Rows</span>-->\n            </div>\n\n            <!--Export "Button"-->\n            <div *ngIf="!settings.hideExportButton" tabindex="0" aria-label="Export" role="button"\n                 id="__cas_grid_control_button_export"\n                 (click)="_exportClicked()" (keydown)="_keydown_exportButton($event)"\n                 class="__cas-grid-control-button {{settings.cssClasses_ControlButton}}"\n                 accesskey="x">\n\n                <div id="__cas_grid_control_export" class="mdi mdi-export __cas-grid-icon {{settings.cssClasses_GridIcon}}"></div>\n                <span style="margin-left:3px;">E<u>x</u>port</span>\n                <!--<span class="off-screen">Exports Table</span>-->\n            </div>\n\n            <!--Settings "Button"-->\n            <div *ngIf="!settings.hideSettingsButton" tabindex="0" aria-label="Settings" role="button"\n                 id="__cas_grid_control_button_settings"\n                 (click)="_settingsClicked()" (keydown)="_keydown_settingsButton($event)"\n                 class="__cas-grid-control-button {{settings.cssClasses_ControlButton}}">\n\n                <div id="__cas_grid_control_settings" class="mdi mdi-settings __cas-grid-icon {{settings.cssClasses_GridIcon}}"></div>\n                <span style="margin-left:3px;">Settings</span>\n                <!--<span class="off-screen">Exports Table</span>-->\n            </div>\n\n        </div>\n\n        <!--Search Box-->\n        <div *ngIf="!settings.hideSearchBox" class="__cas_grid_search_box">\n\n            <!--This property enables auto search (TAB) on input box. Uncomment and move below into the \n                input element to activate this feature.\n                (blur)="_executeSearch($event)"-->\n            <input id="{{_gridId}}__cas_grid_input_search"\n                   [(ngModel)]="_storedFilter"\n                   class="__cas-grid-input-group-left"\n                   (keydown)="_keydown_executeSearch($event)"\n                   onfocus="this.select();"\n                   style="width:150px"\n                   aria-label="Search, Press enter or search button to execute the search"\n                   title="Press enter or search button to execute the search" />\n            <button class="btn btn-primary pt-0 pb-0 __cas-grid-input-group-right"\n                    type="button"\n                    (click)="_executeSearchButton()">Search</button>\n        </div>\n            <div style="border-right:solid 1px #ccc; margin:0 10px; height:80%;"></div>\n\n        <!--Pagination-->\n        <div *ngIf="!settings.hidePaginationControls" class="__cas-grid-pagination {{settings.cssClasses_Pagination}}">\n\n            <!--First-->\n            <div tabindex="0" role="button"\n                 id="__cas_grid_control_button_page_first"\n                 (click)="_changePage(\'first\')" (keydown)="_keydown_changePage($event, \'first\')"\n                 aria-label="First Page" title="First Page"\n                 class="mdi mdi-skip-backward __cas-grid-pagination-icons {{settings.cssClasses_PaginationIcons}}">\n\n                <!--<span class="off-screen">Go to first page</span>-->\n            </div>\n\n            <!--Previous-->\n            <div tabindex="0" role="button"\n                 id="__cas_grid_control_button_page_prev"\n                 (click)="_changePage(\'prev\')" (keydown)="_keydown_changePage($event, \'prev\')"\n                 aria-label="Previous Page" title="Previous Page"\n                 class="mdi mdi-skip-previous __cas-grid-pagination-icons {{settings.cssClasses_PaginationIcons}}">\n\n                <!--<span class="off-screen">Go to previous page</span>-->\n            </div>\n\n            <!--Page Display-->\n            <label style="margin-right:4px;" for="_curPageInput">Page</label>\n            <input id="_curPageInput"\n                   [attr.aria-label]="\'Current Page of\' + totalPages"\n                   [(ngModel)]="this._currentPageInput"\n                   (change)="_currentPageInputChanged()"\n                   style="margin:0;"\n                   class="__cas-grid-pagination-input {{cssClasses_PaginationInput}}" />\n\n            <div>&nbsp;of&nbsp;{{totalPages}}&nbsp;</div>\n\n            <!--Next-->\n            <div tabindex="0" role="button"\n                 id="__cas_grid_control_button_page_next"\n                 (click)="_changePage(\'next\')" (keydown)="_keydown_changePage($event, \'next\')"\n                 aria-label="Go to next page" title="Next Page"\n                 class="mdi mdi-skip-next __cas-grid-pagination-icons {{settings.cssClasses_PaginationIcons}}">\n\n                <!--<span class="off-screen">Go to next page</span>-->\n            </div>\n\n            <!--Last-->\n            <div tabindex="0" role="button"\n                 id="__cas_grid_control_button_page_last"\n                 aria-label="Go to Last Page" title="Last Page"\n                 (click)="_changePage(\'last\')" (keydown)="_keydown_changePage($event, \'last\')"\n                 class="mdi mdi-skip-forward __cas-grid-pagination-icons {{settings.cssClasses_PaginationIcons}}">\n\n                <!--<span class="off-screen">Go to last page</span>-->\n            </div>\n\n            <div class="__cas-grid-rows-per-page-div" title="Rows displayed per page">\n\n                <div style="border-left:solid 1px #ccc; height:15px; margin-right:5px;"></div>\n\n                <label style="margin-right:2px;" for="_rowsPerPage">Rows</label>\n                <input id="_rowsPerPage"\n                       [(ngModel)]="settings.rowsPerPage"\n                       class="__cas-grid-rows-per-page-input {{cssClasses_RowsPerPageInput}}" />\n            </div>\n        </div>\n    </div>\n\n\n    <!--Grid-->\n    <div class="{{ disabled ? \'__cas-grid __cas-grid-disabled\' : \'__cas-grid\' }} {{settings.positionAbsolute ? \'__cas-grid-absolute\' : \'__cas-grid-block\'}} {{settings.cssClasses_Grid}} {{settings.cssClasses_Grid_Disabled}}"\n         [style.minWidth.px]="_gridMinWidth"\n         [style.width.px]="!settings.positionAbsolute && settings.width? settings.width : \'auto\'"\n         [attr.summary]="summary"\n         role="grid">\n\n        <!--Headers-->\n        <!--I removed the extra div because it confuses IE and doesn\'t size properly based on the child content (also, it doesn\'t seem to have added any value)-->\n        <!--<div class="__cas-grid-headers {{settings.cssClasses_Headers}}" role="rowgroup">-->\n        <!--Column header Row-->\n        <div role="row"\n             class="__cas-grid-col-header-row {{settings.cssClasses_ColumnHeaderRow}}"\n             [style.paddingRight.px]="_scrollBarWidth"\n             aria-label="Column headers">\n\n            <!--Checkbox Column Header-->\n            <div role="columnheader"\n                 *ngIf="settings.showCheckboxColumn"\n                 tabindex="0"\n                 class="__cas-grid-col-header-cell {{settings.cssClasses_ColumnHeaderCell}}"\n                 [style.width.px]="settings.showCheckboxColumn ? settings.checkboxColumnWidth : 1"\n                 [style.minWidth.px]="settings.showCheckboxColumn ? settings.checkboxColumnMinWidth : 1">\n                <span class="off-screen">Select this row</span>\n                &nbsp;\n            </div>\n\n            <!--Column Headers-->\n            <div role="columnheader"\n                 tabindex="0"\n                 class="__cas-grid-col-header-cell {{_columnSortable(column) ? \'__cas-grid-col-header-cell-sortable\':\'\'}} {{settings.cssClasses_ColumnHeaderCell}}"\n                 (click)="_sortColumn(column)"\n                 (keydown)="_keydown_columnHeader($event, column)"\n                 [attr.aria-sort]="column.sorted ? (column.sorted==\'D\' ? \'descending\' : \'ascending\') : \'none\'"\n                 [style.width]="column.width"\n                 [style.minWidth.px]="column.minWidth"\n                 *ngFor="let column of settings.columns">\n\n                <!--Ascending Icon-->\n                <div *ngIf="column.sorted==\'A\'"\n                     role="button"\n                     aria-label="Sorted ascending"\n                     class="mdi mdi-arrow-up-bold __cas-grid-col-header-cell-sort-icons {{settings.cssClasses_ColumnHeaderCellSortIcons}}">\n\n                    <span class="off-screen">Sorted Ascending</span>\n                </div>\n\n                <!--Descending Icon-->\n                <div *ngIf="column.sorted==\'D\'"\n                     role="button"\n                     aria-label="Sorted descending"\n                     class="mdi mdi-arrow-down-bold __cas-grid-col-header-cell-sort-icons {{settings.cssClasses_ColumnHeaderCellSortIcons}}">\n\n                    <span class="off-screen">Sorted Descending</span>\n                </div>\n\n                <div data-html="true" data-toggle="tooltip" title="{{column.columnHeaderToolTip ? column.columnHeaderToolTip : column.display}}"\n                     [attr.aria-label]="column.display">{{_getTruncatedColumnHeader(column.display)}}</div>\n\n                <span class="off-screen">,Click to sort</span>\n            </div>\n\n        </div>\n\n        <!--</div>-->\n        <!--Body-->\n        <!--Body:Loading-->\n        <div *ngIf="settings.loading"\n             style="padding:20px; display:flex; align-items:center;"\n             class="{{settings.cssClasses_Body}}"\n             role="presentation">\n\n            <img src="data:image/gif;base64,R0lGODlhEAAQAPIAAP///wAAAMLCwkJCQgAAAGJiYoKCgpKSkiH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJCgAAACwAAAAAEAAQAAADMwi63P4wyklrE2MIOggZnAdOmGYJRbExwroUmcG2LmDEwnHQLVsYOd2mBzkYDAdKa+dIAAAh+QQJCgAAACwAAAAAEAAQAAADNAi63P5OjCEgG4QMu7DmikRxQlFUYDEZIGBMRVsaqHwctXXf7WEYB4Ag1xjihkMZsiUkKhIAIfkECQoAAAAsAAAAABAAEAAAAzYIujIjK8pByJDMlFYvBoVjHA70GU7xSUJhmKtwHPAKzLO9HMaoKwJZ7Rf8AYPDDzKpZBqfvwQAIfkECQoAAAAsAAAAABAAEAAAAzMIumIlK8oyhpHsnFZfhYumCYUhDAQxRIdhHBGqRoKw0R8DYlJd8z0fMDgsGo/IpHI5TAAAIfkECQoAAAAsAAAAABAAEAAAAzIIunInK0rnZBTwGPNMgQwmdsNgXGJUlIWEuR5oWUIpz8pAEAMe6TwfwyYsGo/IpFKSAAAh+QQJCgAAACwAAAAAEAAQAAADMwi6IMKQORfjdOe82p4wGccc4CEuQradylesojEMBgsUc2G7sDX3lQGBMLAJibufbSlKAAAh+QQJCgAAACwAAAAAEAAQAAADMgi63P7wCRHZnFVdmgHu2nFwlWCI3WGc3TSWhUFGxTAUkGCbtgENBMJAEJsxgMLWzpEAACH5BAkKAAAALAAAAAAQABAAAAMyCLrc/jDKSatlQtScKdceCAjDII7HcQ4EMTCpyrCuUBjCYRgHVtqlAiB1YhiCnlsRkAAAOwAAAAAAAAAAAA==" />&nbsp;Loading Data...\n        </div>\n\n        <!--aria-live="polite"-->\n        <div *ngIf="!settings.loading && settings.showMessageWhenEmpty && (!viewRows || viewRows.length == 0)"\n             role="presentation"\n             style="padding:20px;"\n             tabindex="0">\n\n            No records to display\n        </div>\n\n        <!--Body:Loaded-->\n        <div *ngIf="!settings.loading && (viewRows.length > 0 || !settings.showMessageWhenEmpty)"\n             id="body_{{_gridId}}"\n             class="{{ disabled ? \'__cas-grid-body __cas-grid-body-disabled\' : \'__cas-grid-body\' }} {{settings.cssClasses_Body}} {{settings.cssClasses_Body_Disabled}}"\n             [style.height.px]="!settings.positionAbsolute && settings.height ? settings.height : \'auto\'"\n             [style.maxHeight.px]="!settings.positionAbsolute && settings.maxHeight ? settings.maxHeight : \'auto\'"\n             [style.minHeight.px]="50"\n             role="rowgroup">\n\n            <!--Rows-->\n            <div role="row"\n                 tabindex="{{ settings.showCheckboxColumn ? -1 : 0 }}"\n                 class="{{ IsRowOrGridDisabled(row) ? \'__cas-grid-row-disabled\' : \'\'}} {{ IsRowOrGridDisabled(row) ? \'\' : \'__cas-grid-row\' }} {{ (row.__selected && !IsRowOrGridDisabled(row) || row.__highlighted) ? \'__cas-grid-row-selected\' : \'\'}} {{ IsRowOrGridDisabled(row) ? \'\' : settings.cssClasses_Row}}"\n                 (click)="_rowClicked($event, row)"\n                 (keydown)="_keydown_row($event, row)"\n                 [attr.aria-selected]="row.__selected"\n                 [attr.aria-rowindex]="i+1"\n                 aria-label="Press enter key to select row"\n                 *ngFor="let row of viewRows | slice:currentPageStartIndex:currentPageEndIndex; let i=index">\n\n                <!--<span id="__testspan" class="off-screen">Press enter key to select row</span>-->\n                <!--Checkbox Cell-->\n                <div *ngIf="settings.showCheckboxColumn"\n                     id="{{__generateGridCellId(i+1,0)}}"\n                     role="gridcell"\n                     tabindex="0"\n                     [attr.data-row]="i+1"\n                     [attr.data-col]="0"\n                     [attr.data-cas-grid-cell]="1"\n                     [attr.data-cas-checkbox-cell]="1"\n                     class="__cas-grid-chkbox-cell __cas-grid-cell {{settings.cssClasses_Cell}}"\n                     [style.width]="settings.showCheckboxColumn ? settings.checkboxColumnWidth : \'1px\'"\n                     [style.minWidth.px]="settings.showCheckboxColumn ? settings.checkboxColumnMinWidth : 1">\n\n                    <!--Row Identification for JAWS/508-->\n                    <span *ngIf="settings.rowIdentifyForJAWS && row[settings.rowIdentityFieldForJAWS]" class="off-screen">\n                        {{settings.rowIdentityPrefixForJAWS}}{{row[settings.rowIdentityFieldForJAWS]}}\n                    </span>\n\n                    <!--Checkbox indicator-->\n                    <div *ngIf="!settings.disableSelection && settings.showCheckboxColumn && settings.multiSelect"\n                         class="mdi __cas-grid-checkdiv {{ (row.__selected && !IsRowOrGridDisabled(row)) ? \'mdi-checkbox-marked-outline __cas-grid-checkdiv-checked\' : \'mdi-checkbox-blank-outline __cas-grid-checkdiv-unchecked\' }}">\n                    </div>\n\n                    <!--Radio indicator-->\n                    <div *ngIf="!settings.disableSelection && settings.showCheckboxColumn && !settings.multiSelect"\n                         class="mdi __cas-grid-checkdiv {{ (row.__selected && !IsRowOrGridDisabled(row)) ? \'mdi-radiobox-marked __cas-grid-checkdiv-checked\' : \'mdi-radiobox-blank __cas-grid-checkdiv-unchecked\' }}">\n                    </div>\n\n                    <!--More 508 stuff-->\n                    <span *ngIf="!settings.disableSelection" class="off-screen">\n                        Press enter to toggle row selection\n                    </span>\n\n                </div>\n\n                <!--Row Identification for JAWS/508-->\n                <span *ngIf="!settings.showCheckboxColumn && settings.rowIdentifyForJAWS && row[settings.rowIdentityFieldForJAWS]" class="off-screen">\n                    {{settings.rowIdentityPrefixForJAWS}}{{row[settings.rowIdentityFieldForJAWS]}}\n                </span>\n\n                <!--Cells-->\n                <div role="gridcell"\n                     id="{{__generateGridCellId(i+1,j+1)}}"\n                     tabindex="0"\n                     [attr.aria-label]="!!column.ariaCellFn ? _getToolTipForJaws(row, column) : !!column.toolTipFn ? _getToolTip(row, column) : null"\n                     [title]="_getToolTip(row, column)"\n                     [attr.data-row]="i+1"\n                     [attr.data-col]="j+1"\n                     [attr.data-cas-grid-cell]="1"\n                     class="__cas-grid-cell {{settings.cssClasses_Cell}}"\n                     [style.width]="column.width"\n                     [style.minWidth.px]="column.minWidth"\n                     *ngFor="let column of settings.columns; let j=index">\n\n                    <!--=====================================================-->\n                    <!--TEMPLATE: TRUSTING TEMPLATE INPUT FROM CONSUMER-->\n                    <!--=====================================================-->\n                    <!--[title]="column.templateFn(row[column.fieldName])"-->\n                    <div *ngIf="column.templateFn"\n                         [attr.aria-label]="column.templateFn(row[column.fieldName], row)"\n                         [innerHTML]="domSanitizer.bypassSecurityTrustHtml(_preRenderCellProcessing(column, column.templateFn(row[column.fieldName], row)))">\n                    </div>\n\n                    <!--NOTE: Can\'t add carriage return between element declaration and data below as it may impact the white spacing on some browsers-->\n                    <!--=====================================================-->\n                    <!--NO-TEMPLATE - READ ONLY-->\n                    <!--=====================================================-->\n                    <div *ngIf="!column.templateFn && !column.editable"\n                         [attr.aria-label]="!!column.toolTipFn ? _getToolTip(row, column) : null"\n                         [title]="_getToolTip(row, column)"\n                         [style.whiteSpace]="column.nowrap ? \'nowrap\' : null">{{ column.procDataFn ? _preRenderCellProcessing(column, column.procDataFn(row[column.fieldName], row)): _preRenderCellProcessing(column, row[column.fieldName]) }}</div>\n\n                    <!--=====================================================-->\n                    <!--NO-TEMPLATE - EDITABLE (STANDARD EDIT)-->\n                    <!--=====================================================-->\n                    <!--[attr.aria-label]="column.procDataFn ? column.procDataFn(row[column.fieldName], row) : row[column.fieldName]">-->\n                    <div *ngIf="!column.templateFn && column.editable" [style.whiteSpace]="column.nowrap ? \'nowrap\' : null" class="__cas-grid-cell-editable-div {{row[column.fieldName].__cssClasses_EditableCell}}">\n\n                        <!--<span class="off-screen">{{_getToolTip(row, column)}},value,</span>-->\n                        <span class="off-screen">value,</span>\n\n                        <!--EDIT MODE OFF-->\n                        <span *ngIf="_editCellId != __generateGridCellId(i+1,j+1)" class="__cas-grid-cell-inputmarker-span">{{ column.procDataFn ? _preRenderCellProcessing(column, column.procDataFn(row[column.fieldName], row)): _preRenderCellProcessing(column, row[column.fieldName])}}</span>\n\n                        <!--EDIT MODE ON-->\n                        <input *ngIf="_editCellId == __generateGridCellId(i+1,j+1)"\n                               tabindex="0"\n                               class="__cas-grid-cell-input"\n                               onfocus="this.select();"\n                               (change)="_editableCellChanged($event, row, column)"\n                               [attr.aria-label]="_getToolTip(row, column)"\n                               [title]="_getToolTip(row, column)"\n                               value="{{ column.procDataFn ? _preRenderCellProcessing(column, column.procDataFn(row[column.fieldName], row)): _preRenderCellProcessing(column, row[column.fieldName]) }}" />\n\n                        <!--ADORNMENTS-->\n                        <ng-container name="container42" *ngFor="let adornment of row[column.fieldName].__adornments">\n                            <div\n                                 class="mdi {{adornment.mdiName}}"\n                                 [style.color]="adornment.color ? adornment.color : null"\n                                 [style.fontSize]="adornment.sizeEm ? adornment.sizeEm + \'em\' : \'1em\'">\n                            </div>\n                        </ng-container>\n\n                        <span class="off-screen">,Press enter to begin or end editing mode</span>\n\n                    </div>\n\n\n                </div>\n\n            </div>\n\n        </div>\n    </div>\n\n</div>'
})
export class CASGridComponent implements OnInit {


    // INPUTS/OUTPUTS
    //-------------------------------------------------------------------------------------------------------------------------
    @Input() settings;
    @Input() disabled;
    @Input() summary;

    @Output() onClearClicked = new EventEmitter();
    @Output() onExportClicked = new EventEmitter();
    @Output() onPageChanged = new EventEmitter();
    @Output() onRefreshClicked = new EventEmitter();
    @Output() onRowChanged = new EventEmitter();        //this.onRowChanged.emit(row, col, oldVal, newVal);
    @Output() onRowSelected = new EventEmitter();
    @Output() onRowUnSelected = new EventEmitter();
    @Output() onRowSelectionChanged = new EventEmitter(); //Fires for any type of row selection change
    @Output() onSettingsClicked = new EventEmitter();
    @Output() onSearch = new EventEmitter();                //this.onSearch.emit(searchText);
    @Output() onSort = new EventEmitter();                  //this.onSort.emit(column); //column=null for no sorts
    @Output() onValidationFailed = new EventEmitter();      //this.onValidationFailed.emit(message);
    @Output() onCellClicked = new EventEmitter();           //this.onCellClicked(cell)
    

    // GETTERS/SETTERS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // currentPageStartIndex - GET
    //==================================
    get currentPageStartIndex(){
        if (this.settings.manualMode){
            return 0;
        }
        else{
            return ((+this._currentPage) - 1) * (+this.settings.rowsPerPage); //Make SURE this is a number
        }
    };

    //==================================
    // currentPageEndIndex - GET
    //==================================
    get currentPageEndIndex(){
        if (this.settings.manualMode){
            this.viewRows.length;
        }
        else{
            return this.currentPageStartIndex + (+this.settings.rowsPerPage); //Make SURE this is a number
        }
    };

    //==================================
    // totalPages - GET
    //==================================
    get totalPages(){
        if (this.settings.manualMode){
            return this.settings.totalPages;
        }
        else{
            return Math.ceil(this.viewRows.length / this.settings.rowsPerPage);
        }
    };

    //==================================
    // viewRows - GET
    //==================================
    // Returns the current rows to be diplayed in the grid (includes filter when applicable)
    get viewRows() {

        if(this._filteredData !== null){
            return this._filteredData;
        }
        else{
            return this.settings.rows || [];
        }
    }

    // INIT PUBLIC MEMBERS
    //-------------------------------------------------------------------------------------------------------------------------
    settings = {};


    // PRIVATE MEMBERS
    //-------------------------------------------------------------------------------------------------------------------------

    //The current page is managed internally and shouldn't be changed directly
    _currentPage = 1;

    //This is used to "buffer" the displayed page
    _currentPageInput = 1;

    //To support 508 (not currently applied to custom templates).  When edit mode is on, cells will display an edit box if appropriate
    //Otherwise, they stay as read only divs the arrow key navigation can work.  Pressing <enter> while in an editable cell or clicking that 
    //cell will engage edit mode.  Pressing <enter> again while in a cell will terminate edit mode.
    //_editMode = 0;
    _editCellId = null; //The id of the cell currently being edited

    //If a filter is set for the grid's data, it will be stored here until changed
    //(this is to prevent having to refilter the data every time it is asked for)
    _filteredData = null;

    //Set to null for no filters
    _filters = null; //{ name<str>: fn<function>, ...  }

    //Each grid needs a unique id so multiple girds on the page don't collide
    _gridId = null;

    //Sum of the column min widths plus a little extra
    _gridMinWidth = null; 

    //The grid name must be set for the SetFocus... functions to work 
    //(it will work by default without setting a unique name if there is only one grid on the page)
    //_gridName = "__cas_grid";

    //Percentage based widths deduct the scroll bar from their calculations.  
    //We need to add it back in to keep the column headers and row cells synced.
    _scrollBarWidth = null; 

    //This is stored so we can provide it to the consumer on demand
    _selectedRow = null;

    //Init this value as "" to avoid any null check issues later if it's never touched before a search is executed
    _storedFilter = ""; //model data for the search box on the markup

    // Any settings below can be overidden on the settings input variable from the grid consumer.
    // Any omitted property will copy the default value to the settings input variable.
    _default_settings = {

        //Set this to true if you want to place a custom value into blank cells
        allowEmptyCells:false,
        blankCellValue:"--", //Changed to match expectactions from UX team

        //Only used when showCheckboxColumn = true
        checkboxColumnWidth:"1%",
        checkboxColumnMinWidth:25, //minWidth is assign in px

        //Default filter applied when the grid is first rendered
        defaultFilters:null, //{ name<str>: fn<function>, ...  }

        //When true, the grid will only fire an export event, it will not actually do the export
        disableInternalExport:false,

        //Disable selection
        disableSelection:false,

        exportTitleAddon:"",       //String to add to end of export name

        //Grid ID
        gridId:null,    //The control will autogenerate unique ids for each grid on the page by default, 
                        //however you cna overrid the id here.  It is used as a prefix for most child control ids
                        //as well as the grid.  Make sure not to let this duplicate another grid on the page.

        //Height in px of Grid BODY (set to a numerical value i.e.:80, ignored if positionAbsolute = true or if set to 0)
        height:0,

        //Option to hide entire header rows
        hideControlRow:false,

        //Options to hide individual controls
        hideClearSelectedButton:false,      //Recommended that you only hide this if you are not using multiSelect
        hidePaginationControls:false,       //Set to true if you know you wont need these and don't want to see them rendered
        hideRefreshButton:false,
        hideSearchBox:true,
        hideSettingsButton:true,

        //When true, display a "loading" image
        //NOTE: Don't call this directly anymore (except for initialization).  Use the function MarkAsLoading(bool) instead
        loading:false,

        //Set manual mode to true to manage all pagination and sorting externally
        // (Experimental: this hasn't been tested in a long while and may need some attention)
        manualMode:false,

        //Number of characters before a column header's text is truncated.
        maxColHeaderLength:35,

        //Height in px of Grid BODY (set to a numerical value i.e.:80, ignored if positionAbsolute = true or if set to 0 )
        maxHeight:0,

        //When true, multiple rows can be selected
        multiSelect:false,

        //If true, this grid will be positioned absolutely with all extents set to 0.  Be sure to
        //place the grid in an absolute or relative parent if you enable this.
        positionAbsolute:false, 

        //When true, the field identified by "rowIdentityFieldForJAWS" will be placed in the checkbox
        //cell (off screen) such that JAWS will read that item for users to speed up the process of
        //identifying a row when using only a screen reader.
        //ONLY works if you show the checkbox!
        rowIdentifyForJAWS:false,
        rowIdentityFieldForJAWS:null,
        rowIdentityPrefixForJAWS: "", //(optional) Text that preceeds the id info so JAWS says something sensible

        //Ignored when manual mode is true (presumed that host controls rows per page themselves)
        rowsPerPage:50, 

        //When true, an empty grid is replaced with a "no records" message when empty
        showMessageWhenEmpty:true,

        //Checkbox column will be visible and synced to selected rows
        //(Note that the checkbox column will take up a set number of pixels.  If all other columns
        //are sized to sum to 100%, the total grid size may be oversized by the width of the checkbox)
        showCheckboxColumn:true,

        //When true, all columns will be flagged sortable unless otherwise noted
        sortByDefault:true,

        //Pages are one-indexed.  This is only used once at nginit
        startingPage:1,

        //Ignored unless in manual mode
        totalPages:null,        

        //Width in px (set to a numerical value i.e.:80, ignored if positionAbsolute = true )
        width:0,

        // Any specified css classes will be added in ADDITION to their default styles (use !important to override)
        //---------------------------------------------------------------------------------------------------------------
        //  Note: You may need to set the encapsulation property in the parent component's definition for their
        //  classes to be accessible to the child grid:
        //
        //    import { ...., ViewEncapsulation } from '@angular/core';
        //
        //    @Component({
        //        selector:'your-selector',
        //        <...other stuff...>
        //        encapsulation: ViewEncapsulation.None //Allows child components to read the parent's styles
        //    })
        //---------------------------------------------------------------------------------------------------------------
        cssClasses_Grid:"", 
        cssClasses_Grid_Disabled:"",                //__cas-grid-disabled
        //cssClasses_Headers:"",                    //__cas-grid-headers //Depricated
        cssClasses_ControlRow:"",
        cssClasses_ColumnHeaderRow:"", 
        cssClasses_ColumnHeaderCell:"", 
        cssClasses_ColumnHeaderCellSortIcons:"",
        cssClasses_Body:"",                         //__cas-grid-body
        cssClasses_Body_Disabled:"",                //__cas-grid-body-disabled
        cssClasses_Row:"", 
        cssClasses_Cell:"",
        cssClasses_Controls:"", 
        cssClasses_ControlButton:"", 
        cssClasses_GridIcon:"",
        cssClasses_Pagination:"", 
        cssClasses_PaginationIcons:"", 
        cssClasses_PaginationInput:"",
        cssClasses_RowsPerPageInput:"",

        // Columns Settings
        //---------------------------------------------------------------------------------------------------------------
        //fieldName, display, and width are currently required
        //  options:
        //
        //      columnHeaderToolTip<str>:   Tooltip string for column headers
        //
        //      dataType<string>:           (opt, string) Can be: date, int, or string.  Currently used primarily for sorting.
        //                                  Since column settings aren't explicitly defaulted anywhere, the logic throughout
        //                                  will presume the data type is a string if this setting is omitted.
        //
        //      display<str>:               Display for this column's header
        //
        //      editable<bool>:             When true, the cell will be an editable textbox that manages the row data in that cell
        //                                  IGNORED when templateFn is used.
        //
        //      editDataFn<fn>:             SIGNATURE: column.editDataFn(cell, oldVal, newVal);
        //                                  WARNING: This method must be synchronous, or the "row changed" event will trigger immediately from the grid.
        //                                  Use this function to override the data edit process. Instead of the value being written
        //                                  directly, this method will be called with the cell value and the new value (useful for when
        //                                  you are using an object for a ceel value and want to interact with a property on that object.
        //                                  Can allow the user of referential updates against datasets.)
        //
        //      fieldName<str>:             Name of the field that will match a property on the row objects
        //
        //      max<int>:                   When numeric and editable, max allowed numeric input
        //
        //      min<int>:                   When numeric and editable, min allowed numeric input
        //
        //      minWidth<int>:              Total column minWidths used to calculate grid minWidth: __getGridMinWidth()
        //
        //      nowrap<bool>:               Set to true and it will prevent text from wrapping inside a cell.
        //
        //      numeric<bool>:              Indicates that the editable field should only contain numbers
        //
        //      procDataFn<fn>:             Cell data will be run through this function before its added to the grid (sanitized and escaped)
        //                                  Signature: (oldVal, row)
        //                                  ex1: procDataFn:(startDate, row) => { return this._formatDate(startDate); }
        //                                  ex2: this._formatDate.bind(this) //parameter is the current field, passed in by default
        //
        //      sortable<bool>:             Set to true to allow column to be sorted
        //
        //      templateFn<fn>:             If defined, each cell will pass its data to this function and render it AS !!TRUSTED!! HTML
        //                                  (BE CAREFUL WHAT YOU ALLOW TO RENDER DYNAMICALLY)
        //                                  Overrides "editable".
        //
        //                                  Note: Make sure your HTML doesn't block click events up to the grid if you want the grid events to work.
        //
        //      toolTipFn<fn>:              Called for each cell to generate a tooltip (overrides procDataFn if one had been provided): toolTipFn(cellValue, row)
        //
        //      ariaCellFn<fn>:             Called for each cell to generate a specific aria label for cell: ariaCellFn(cellValue, row)
        //
        //      truncate<int>: Set to a numeric value and the cell's (string) content will be truncated to that length
        //
        //      width<str>:                 Can be px of % (haven't tried mixing them)
        //
        //      ---------------------------------------------------------------------------------------------------------

        columns:[
            { fieldName:'col1', display:'Col 1', width:'33%', minWidth:300, truncate:50, nowrap:true },
            { fieldName:'col2', display:'Col 2', width:'33%', minWidth:50 },
            { fieldName:'col3', display:'Col 3', width:'33%', minWidth:50, sortable:true, sorted:'A' } //'A' = Ascending, 'D" = Descending
        ],

        // Row Settings
        //---------------------------------------------------------------------------------------------------------------
        // Generally rows just contain data, but there are a few settings for cells
        //
        //  __cssClasses_EditableCell:  //(__cas-grid-cell-editable-div) This is the div that wraps the input box and adornments in the 
        //                                  non-template, editable divs.  It can altered on a per cell basis
        //
        //  __adornments<[{}]>:         //(optional, arr[objects]): These objects indicate mdi icons that should be displayed in the cell.
        //                                  The declaration format is as follows:
        //                                    { 
        //                                        mdiName<str>:   ex: "mdi-alert-circle"
        //                                        color<str>:     ex: "red" ("#FF0000" should also work)
        //                                        sizeEm<int>:    ex: 1.8
        //                                    }
        //
        //  __selected<bool>:           //If true, this row will be selected
        
        //This is sample data that will be displayed until you replace it
        rows:[
            { col1:'A01', col2:'B01', col3:'Carrot' },
            { col1:'A02', col2:'B02', col3:'Potato' },
            { col1:'A03', col2:'B03', col3:'Apple', __selected:true }, //Only the first selected item will be honored
            { col1:'A04', col2:'B04', col3:'Banana' },
            { col1:'A05', col2:'B05', col3:'Romaine' },
            { col1:'A06', col2:'B06', col3:'Raddish' },
            { col1:'A07', col2:'B07', col3:'Turnip' },
            { col1:'A08', col2:'B08', col3:'Celery' },
            { col1:'A09', col2:'B09', col3:'Grape' },
            { col1:'A10', col2:'B10', col3:'Orange' },
            { col1:'A11', col2:'B11', col3:'Avacado' },
            { col1:'A12', col2:'B12', col3:'Tomato' }
        ]

        // Cell Settings
        //---------------------------------------------------------------------------------------------------------------
        // Cells can have primitive values and be rendered directly by the grid.  However, they can also have complex objects
        //  and their rendering can be handled by the procDataFn value.  In these cases, the grid supports some more complex behavior
        //  such as rendering adornments or changing the style of the editable cell's containing div.
        //  (If I ever build one of these again, I think I'll default all cell values to an object with a standard value property so
        //  consumers won't have to run a custom procDataFn just to use these extra features).
        //
        //  __adornments<[{}]>:         //(optional, arr[objects]): These objects indicate mdi icons that should be displayed in the cell.
        //                                  The declaration format is as follows:
        //                                    { 
        //                                        mdiName<str>:   ex: "mdi-alert-circle"
        //                                        color<str>:     ex: "red" ("#FF0000" should also work)
        //                                        sizeEm<int>:    ex: 1.8
        //                                    }
        //
        //  __cssClasses_EditableCell:  //(__cas-grid-cell-editable-div) This is the div that wraps the input box and adornments in the 
        //                                  non-template, editable divs.  It can altered on a per cell basis
        


    };

 
    // INITIALIZATION
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // constructor
    //==================================
    constructor(domSanitizer:DomSanitizer){
        this.domSanitizer = domSanitizer;
    };

    //==================================
    // ngOnInit
    //==================================
    ngOnInit(){

        // Valiate Inputs
        //-------------------------------------
        this.settings = this.settings || {};
        
        // Generate a unique id for this grid
        //-------------------------------------
        this.__generateGridId();
        
        // Merge Settings
        //--------------------
        //We need to bind the UI to the (input) settings property so any changes made by the grid consumer
        //will be reflected by angular in the markup.

        //Grab the properties defined on the default settings that are not defined in the input settings
        var keysToCopy = Object.keys(this._default_settings).filter(k=>!(k in this.settings));

        //Merge inputs onto member properties (so we can have defaults)
        keysToCopy.map((key)=>{ this.settings[key] = this._default_settings[key]; });

        // Do some grid calculations
        //-------------------------------------
        this._scrollBarWidth = this.__getScrollbarWidth();
        this._gridMinWidth = this.__getGridMinWidth();

        // Check Heights
        //-------------------------------------
        ////IE craps itself if you set a max-height but no height (at least it does in this flex display)
        //if (this.settings.maxHeight && !this.settings.height){
        //    this.settings.height = this.settings.maxHeight;
        //}

        // Pagination
        //-------------------------------------
        this._currentPage = this._isPageValid(this.settings.startingPage) ? this.settings.startingPage : 1;
        this._currentPageInput = this._currentPage;

        // Default Sort Specified?
        //-------------------------------------
        // If we are not in manual mode, check to see if a default sort has been specified
        // NOTE: This won't really work if the data is not already loaded.  You'll need to call a new sort after the load.
        if (!this.settings.manualMode){
            var sortedColumns = this.settings.columns.filter(c=>!!c.sorted);
            if (sortedColumns.length > 0){
                this._sortColumn(sortedColumns[0], false); //only sort the first one we find
            }
        }

        //Apply a default filter?
        if (this.settings.defaultFilters !== null) {
            this.SetFilters(this.settings.defaultFilters, true);
        }

        //console.log("[cas-grid:ngOnInit()] settings:", this.settings);
    };


    // EVENT HANDLERS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // _clearRowsClicked
    //==================================
    _clearRowsClicked(){
        if (this.disabled){ return; }
        this.onClearClicked.emit();
        this.UnselectAllRows();
    };

    //==================================
    // _currentPageInputChanged
    //==================================
    _currentPageInputChanged(){
      
        if (this.disabled){ return; }

        //Check to see if this is a valid request
        if (!this._isPageChangeValid(this._currentPageInput)){
            this._currentPageInput = this._currentPage;
            return;
        }
        
        this._setCurrentPage(this._currentPageInput);
    };

    //================================== 
    // _editableCellChanged
    //================================== 
    _editableCellChanged($event, row, column) {

        let oldVal = row[column.fieldName];     //Value from data (could be an object)
        let newVal = $event.target.value;       //Text from the input box

        //Handle numeric fields
        if (column.numeric){

            //If the new value is not a number, revert to the previous value
            if (isNaN(newVal)){
                $event.target.value = column.procDataFn ? column.procDataFn(oldVal, row) : oldVal;
                this.onValidationFailed.emit("Field only accepts numeric values");
                return;
            }

            //Numeric Constraints
            //MIN
            if (typeof column.min != undefined && column.min != null){
                if (newVal < column.min){
                    //Number is too small
                    $event.target.value = column.procDataFn ? column.procDataFn(oldVal, row) : oldVal;
                    this.onValidationFailed.emit("Field must not be less than " + column.min);
                    return;
                }
            }

            //MAX
            if (typeof column.max != undefined && column.max != null){
                if (newVal > column.max){
                    //Number is too large
                    this.onValidationFailed.emit("Field must not exceed " + column.max);
                    $event.target.value = column.procDataFn ? column.procDataFn(oldVal, row) : oldVal;
                    return;
                }
            }

        }

        //Does the caller want to handle updates?
        if (column.editDataFn){

            let cell = row[column.fieldName];
            column.editDataFn(cell, oldVal, newVal);

            //Notify
            this.onRowChanged.emit(row, column, oldVal, newVal);

        }
        else{
            //Sync the row data with the updated value
            row[column.fieldName] = newVal;

            //Notify
            this.onRowChanged.emit(row, column, oldVal, newVal);
        }
    }

    //==================================
    // _executeSearch
    //==================================
    _executeSearch(event){
        let newSearch = event.target.value.toLowerCase();
        this.onSearch.emit(newSearch);
    }

    //==================================
    // _executeSearchButton
    //==================================
    _executeSearchButton() {
        let newSearch = this._storedFilter.toLowerCase();
        this.onSearch.emit(newSearch);
    }

    //==================================
    // _exportClicked
    //==================================
    _exportClicked(){
        if (this.disabled){ return; }
        this.onExportClicked.emit(); 
        
        //Do we do the export internally?
        if (!this.settings.disableInternalExport){
            this.ExportData();
        }
    };

    //==================================
    // _grid_keydown
    //==================================
    //Handle things here that you want to process BEFORE they have occured and may wish to prevent the default behavior,
    //such as ARROW KEYS
    _grid_keydown(event) {

        // Handle <ARROW> keys
        // L=37, U=39, R=40, D=41
        //---------------------------------
        if (event.keyCode < 41 && event.keyCode > 36) {
            
            event.preventDefault();
            event.stopPropagation();

            //LEFT
            if (event.keyCode == 37) {
                this.MoveFocusLeft();
            }

            //RIGHT
            if (event.keyCode == 39) {
                this.MoveFocusRight();
            }

            //UP
            if (event.keyCode == 38) {
                this.MoveFocusUp();
            }

            //DOWN
            if (event.keyCode == 40) {
                this.MoveFocusDown();
            }
        }

    };

    //==================================
    // _grid_keyup
    //==================================
    //Handle things here that you want to process AFTER they have occurec, such as TAB key presses
    _grid_keyup(event) {

        // Handle <ENTER> key
        //---------------------------------
        if (event.keyCode == 13) {

            //Grab the currently selected cell information
            var selectedCell = this.GetSelectedCell();

            //Notify of a cell click
            if (selectedCell.elCell) {
                this.onCellClicked.emit(selectedCell);
            }

            //Boundary: Special handling if this is a checkbox cell
            if (selectedCell.isCheckbox) {
                
                //Disable any edit mode
                this._editCellId = null;

                //Toggle row selection
                this._toggleRow(selectedCell.row);

                //Nothing else to do
                return;
            }

            //If edit mode is on
            if (this._editCellId) {

                //Turn edit mode off
                this._editCellId = null;

                //Move the focus to the parent gridCell if it's not in one
                var elParentGridCell = document.activeElement.closest("[role='gridcell']");
                if (elParentGridCell) { elParentGridCell.focus(); }
                

            }
            else {

                //Turn ON if in an editable cell
                if (selectedCell.elCell) {

                    //Make sure this column is editable before marking it in editing mode
                    if (!selectedCell.col.templateFn && selectedCell.col.editable) {

                        this._editCellId = selectedCell.elCell.id;

                        //Select first tabbable control in the new cell
                        this._setFocusToFirstTabable(selectedCell.elCell);
                    }
                }
        
            }

        }

        // Handle <TAB> key
        //---------------------------------
        if (event.keyCode == 9) {

            //If edit mode is OFF, then there's nothing else to do
            if (!this._editCellId) {
                return;
            }
    
            //Edit mode is ON

            //Grab the currently selected cell
            var selectedCell = this.GetSelectedCell();

            //If the new active element is not a grid cell or is not editable, clear edit mode and jump out
            //(allowing this to work on selectedCell.elCell.col.column.templateFn for now, unless that causes trouble)
            if (!selectedCell.elCell || selectedCell.isCheckbox || !selectedCell.col || !selectedCell.col.editable) {
                this._editCellId = null;
                return; 
            }

            //Selected element *is* an editable grid cell

            //If the new cell is the same as the current cell, then the user has pressed shift tab from the first
            //editable element in the cell.
            if (selectedCell.elCell.id == this._editCellId) {
                
                //SHIFT TAB

                //Can't figure out repro the error condition here, but this triggers ocassionally. Seems to have no effect.
                //validate the hypothesis here just in case we missed something
                //if (!event.shiftKey) { throw "[_grid_keydown] Tab key was pressed and the currently selected cell suggests a SHIFT+TAB, but the shift key is not indicated in the keyboard event."; }

                //Grab the preceeding sibling element and convert it into a cell if possible
                var prevSiblingCell = this.GetCellById(selectedCell.elCell.previousElementSibling.id);

                //If the preceeding sibling element is not a cell, cancel edit mode and jump out
                if (!prevSiblingCell.elCell) {
                    this._editCellId = null;
                    return;
                }

                //If the preceeding element is a non-editable cell, go ahead and jump to that cell before clearing edit mode and exiting
                if (!prevSiblingCell.col.editable) {
                    prevSiblingCell.elCell.focus();
                    this._editCellId = null;
                    return;
                }

                //Previous sibling *is* editable grid cell, select the first tabbale element
                //switch the edit mode over to the new cell
                this._editCellId = prevSiblingCell.elCell.id;

                //Set the focus to the first tabable control
                this._setFocusToFirstTabable(prevSiblingCell.elCell);
            }
            else {

                //TAB
                //We already know that the new cell is an editable grid cell, and that it's id is different
                //than the cell currently marked for ediing.
                
                //Swap edit mode to new cell
                this._editCellId = selectedCell.elCell.id

                //Select first tabbable control in the new cell
                this._setFocusToFirstTabable(selectedCell.elCell);
                
            }

        }

    }

    //==================================
    // _grid_mouseup
    //==================================
    _grid_mouseup(event) {

        //COMPATIBILITY CODE (IE11)
        //In IE 11, elements inside grids don't seem to pass the focus up to the first, tabbable, parent on mouse clicks.  
        //So, a little manual work here to get it to work the same as Chrome.
        var elParentGridCell = document.activeElement.closest("[role='gridcell']"); //"closest" Added to: src\polyfill.js
        if (elParentGridCell && document.activeElement !== elParentGridCell) { elParentGridCell.focus(); }
        ////COMPATIBILITY CODE ^^

        //Check to see if a grid cell has been selected
        var selectedCell = this.GetSelectedCell();

        //Notify of a cell click
        if (selectedCell.elCell) {
            this.onCellClicked.emit(selectedCell);
        }

        //Boundary: Special handling if this is a checkbox cell
        if (selectedCell.isCheckbox) {
                
            //Disable any edit mode
            this._editCellId = null;

            //Toggle row selection
            this._toggleRow(selectedCell.row);

            //Nothing else to do
            return;
        }

        //Has the user selected an editable grid cell?
        if (selectedCell.elCell && selectedCell.col.editable) {

            //Select the new cell for editing
            this._editCellId = selectedCell.elCell.id;
            this._setFocusToFirstTabable(selectedCell.elCell);
        }
        else {
            //Clear edit mode
            this._editCellId = null;
        }

    }

    //==================================
    // _keydown_clearRowsButton
    //==================================
    _keydown_clearRowsButton(event){
        if (this.disabled){ return; }
        if (event.keyCode == 13 || event.keyCode == 32){
            this._clearRowsClicked();
        }
    };

    //==================================
    // _keydown_executeSearch
    //==================================
    _keydown_executeSearch(event){
        if (event.keyCode != 13 || this.disabled){ return; }
        this._executeSearch(event);
    }

    //==================================
    // _keydown_exportButton
    //==================================
    _keydown_exportButton(event){
        if (this.disabled){ return; }
        if (event.keyCode == 13 || event.keyCode == 32){
            this._exportClicked();
        }
    };

    //==================================
    // _keydown_refreshButton
    //==================================
    _keydown_refreshButton(event){
        if (this.disabled){ return; }
        if (event.keyCode == 13 || event.keyCode == 32){
            this._refreshClicked();
        }
    };

    //==================================
    // _keydown_settingsButton
    //==================================
    _keydown_settingsButton(event){
        if (this.disabled){ return; }
        if (event.keyCode == 13 || event.keyCode == 32){
            this._settingsClicked();
        }
    };
    
    //==================================
    // _keydown_changePage
    //==================================
    //  Command should be: 'first', 'prev', 'next', 'last'
    _keydown_changePage(event, command){
        if (this.disabled){ return; }
        if (event.keyCode == 13 || event.keyCode == 32){
            this._changePage(command);
        }
    };

    //==================================
    // _keydown_columnHeader
    //==================================
    _keydown_columnHeader(event, column){
        if (this.disabled){ return; }
        if (event.keyCode == 13 || event.keyCode == 32){
            this._sortColumn(column);
        }
    };

    //==================================
    // _keydown_row
    //==================================
    _keydown_row(event, row){

        if (event.keyCode == 13 || event.keyCode == 32){

            if (!this.settings.showCheckboxColumn) {
                this._toggleRow(row);
            }
        }
    };

    //==================================
    // _refreshClicked
    //==================================
    _refreshClicked(){
        if (this.disabled){ return; }
        this.onRefreshClicked.emit(null);
    };

    //==================================
    // _rowClicked
    //==================================
    _rowClicked(event, row){
        if (!this.settings.showCheckboxColumn) {
            this._toggleRow(row);
        }
    };

    //==================================
    // _settingsClicked
    //==================================
    _settingsClicked(event){
        if (this.disabled){ return; }
        this.onSettingsClicked.emit(null);
    };


    // PUBLIC FUNCTIONS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // ApplyFilters
    //==================================
    //Applies all filters stored in this._filters
    ApplyFilters(){

        //Clear the filtered data and jump out if there are no filters or data present
        if (!this._filters || !this.settings.rows || this.settings.rows.length == 0){
            this._filteredData = null;
            return;
        }

        //Var to hold copy of data while we eork on it (so angular doesn't try to update each time we execute a filter against the actual data)
        let filteredData;

        //Grab the filter names (trying to be kind to IE 11 here)
        let arrFilterNames = Object.keys(this._filters);

        //Apply the filters
        for (let i=0; i<arrFilterNames.length; i++){

            let filterName = arrFilterNames[i];
            let filterFn = this._filters[filterName];

            //If this is the first pass, filter from the rows
            if (i==0){
                filteredData = this.settings.rows.filter( row => filterFn(row) );
            }
            else{
                filteredData = filteredData.filter( row => filterFn(row) );
            }
        }

        //Store the filtered data
        this._filteredData = filteredData;        
    }

    //==================================
    // ClearFilters
    //==================================
    ClearFilters(){
        this._filters = null;
        this._filteredData = null;
        this._changePage('first');
    };

    //==================================
    // ClearFilter
    //==================================
    // apply<bool>:         //(opt, false) When true, this filter is applied 
    ClearFilter(filterName, apply){

        //Jump out if there are no filters
        if (!this._filters){ return; }

        apply = apply || false;

        //Do nothing if this property does not exist
        if (!this._filters.hasOwnProperty(filterName)){
            return;
        }

        //Remove the property
        delete this._filters[filterName];

        //If there are no keys left in the the filters property, go ahead an set it to null
        if (Object.keys(this._filters).length == 0){
            this._filters = null;
        }

        //Reapply the filters
        if (apply){ 
            this.ApplyFilters(); 
            this._changePage('first');
        }
    };

    //==================================
    // ClearRows
    //==================================
    // Clears the rows in settings as well as the filtered data
    ClearRows() {
        this.settings.rows = [];
        this.ClearFilters();
    }

    //==================================
    // ClearSearchBox
    //==================================
    ClearSearchBox(){
        document.getElementById(this._gridId + "__cas_grid_input_search").value = "";
    }

    //==================================
    // ClearSort
    //==================================
    //This doesn't change the grid's sort order, but it clears the sort identifier.
    ClearSort(){
        setTimeout(()=>{
            this.settings.columns.filter(c=>!!c.sorted).map((col)=>{ col.sorted = null; });
            this.onSort.emit(null);
        }, 0)
    }; 

    //==================================
    // DisableRows
    //==================================
    // rows<arr<obj>>           //Pass in the rows to be disabled
    DisableRows(rows){
        for (let i=0; i<rows.length; i++){
            let row = rows[i];
            row["__disabled"] = true;
        }
    };

    //==================================
    // EnableRows
    //==================================
    // rows<arr<obj>>           //(opt, null) Pass in the rows to be disabled or null to enable them all.
    EnableRows(rows){
        rows = rows || this.viewRows;
        for (let i=0; i<rows.length; i++){
            let row = rows[i];
            row["__disabled"] = false;
        }
    };

    //==================================
    // ExportData
    //==================================
    // bFilteredRows<bool>:     //(opt, true) Use filtered rows or entire dataset
    ExportData(bFilteredRows){

        if (bFilteredRows === undefined || bFilteredRows === null){ bFilteredRows = true; }
        var exportRows = bFilteredRows ? this.viewRows : this.settings.rows;

        if (exportRows.length == 0){ return; }

        //Add in the formatted headings to the ouput
        var csv = this.settings.columns.map((colDef)=> '"' + colDef.display.replace('"', '""') + '"') + "\n";
        
        //Add each row based on the headings from the first one
        for (var i=0; i<exportRows.length; i++){
            var row = exportRows[i];
            var rowString = "";
           
            for (var j=0; j < this.settings.columns.length; j++){
                
                var columnDef = this.settings.columns[j];
                var heading = columnDef.fieldName;
                var cellValue = row[heading];

                //Be sure to use and processing function that is specified for the display output
                if (columnDef.procDataFn){
                    cellValue = columnDef.procDataFn(cellValue, row);
                }

                if (this.__isBlank(cellValue))
                    rowString += '""';
                else if (!isNaN(cellValue))
                    rowString += '"=""' + cellValue.toString() + '"""';
                else
                    rowString += '"' + cellValue.toString().replace(/"/g, '""') + '"';
                
                if (j+1 != this.settings.columns.length){
                    rowString += ",";
                }
            }

            csv += (rowString + "\n");
        }
        
        //Popup in new window
        const BOM = '\uFEFF';
        var fileName = 'CPDES.csv';
        if (this.settings.exportTitleAddon)
        {
            fileName = 'CPDES-' + this.settings.exportTitleAddon + '.csv';
        }
        var blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
        if( window.navigator.msSaveOrOpenBlob ){
            window.navigator.msSaveOrOpenBlob(blob, fileName);
        }
        else{
            let link=document.createElement('a');
            link.href=window.URL.createObjectURL(blob);
            link.download= fileName;
            link.click();
        }

        //Old approaches (keeping these snippets for a while, just in case)
        /*
        // NOTES: I'm very confused as to why we did this given that all this information is in the column
        // definitions, and in the order the user expects. Perhaps I wanted to show columns that weren't
        // displayed on the web page, but there's no need for that.

        //Get the properties of the first row to serve as headings (ignore meta data)
        var headings = Object.keys(exportRows[0])
            .filter(k=>!(k.startsWith("__")) && this.GetColumnDefinition(k) != null);

        var headingDisplays = headings
            .map(h=>this.GetColumnDefinition(h).display);

        ...

        //var csv = headings.map((h)=> '"' + h.replace('"', '""') + '"') + "\n";
        //var csv = headingDisplays.map((h)=> '"' + h.replace('"', '""') + '"') + "\n";

        ...

        //for (var j=0; j<headings.length; j++){

        */

    };

    //==================================
    // GetCellById
    //==================================
    //  elementId<str>:     //The HTML id value of the element to return
    //
    //  returns: { 
    //              elCell<obj>:            //HTML Element for the identified gridcell
    //              rowIndex<int>:          //Row index as determined by the angular generator (based on viewRows(), which includes filtering from settings.rows)
    //              colIndex<int>:          //Column index based off the settings.columns array
    //              row<obj>:               //The actual row object data this cell is built from
    //              col<obj>:               //The column definition for the selected column
    //              isCheckbox<bool>:       //True if this is a checkbox cell
    //           }
    //  cell will be null of the active element is not a cell
    GetCellById(elementId) {

        var ret = { elCell:null, rowIndex:null, colIndex:null, row:null, col:null, isCheckbox:null };

        var elCell = document.getElementById(elementId);
        if (!elCell) { return ret; }

        var isGridCell = !!(+elCell.getAttribute("data-cas-grid-cell"));
        if (!isGridCell) { return ret; }

        //Extract the row and column (force to int)
        var rowIndex = elCell.getAttribute("data-row");
        var colIndex = elCell.getAttribute("data-col");
        ret.elCell = elCell;
        ret.rowIndex = +rowIndex;
        ret.colIndex = +colIndex;

        ret.isCheckbox = !!(+elCell.getAttribute("data-cas-checkbox-cell"));

        //The column definition and row arrays use a 0-based index, but we numbered the cells in the markup
        //starting from 1,1 to make room for the checkbox column and the column headers, so we have
        //to make some adjustments here (i.e. the -1).
        ret.row = this.viewRows[this.currentPageStartIndex + (rowIndex-1)];
        //ret.row = this.viewRows[rowIndex-1];
        ret.col = ret.isCheckbox ? null : this.settings.columns[colIndex-1];

        return ret;
    }

    //==================================
    // GetColumnDefinition
    //==================================
    //Searches the column definitions for a given field name and returns the
    //corresponding column definition if found.  This has to be done at runtime
    //because the the column defs can change at any time, and we currently have no
    //wrapper around changes to that property.  Should still be plenty fast though.
    GetColumnDefinition(fieldName){
        var matches = this.settings.columns.filter(c=>c.fieldName == fieldName); //should only be one
        return matches.length > 0 ? matches[0] : null;
    };

    //==================================
    // GetFirstSelectedRow
    //==================================
    // Returns an the first selected rows (or null if no rows are selected)
    GetFirstSelectedRow(){
        let rows = this.viewRows.filter(r => r.__selected == true);
        return rows.length > 0 ? rows[0] : null;
    };

    //==================================
    // GetSelectedCell
    //==================================
    // See GetCellById() for output
    GetSelectedCell() {
        return this.GetCellById(document.activeElement.id);
    }

    //==================================
    // GetSelectedRows
    //==================================
    // Returns an array of the selected rows (or an empty array if no selected rows found)
    GetSelectedRows(){
        return this.viewRows.filter(r => r.__selected == true);
    };

    //==================================
    // GetSelectedRowsIndexes
    //==================================
    // Returns an array of the indexes of selected rows (or an empty array if no selected rows found)
    // Note, these reults are dependent on the current sort order and filter and will be invalid if either changes
    GetSelectedRowsIndexes(){

        let arrIndexes = [];

        for (let i = 0; i < this.viewRows.length; i++) {
            if (this.viewRows[i].__selected == true) {
                arrIndexes.push(i);
            }
        }

        return arrIndexes;
    };

    //==================================
    // GoToFirstPage
    //==================================
    GoToFirstPage(){
        this._changePage('first');
    };

    //==================================
    // IsRowDisabled
    //==================================
    IsRowDisabled(row){
        //Return false if there is no specification on the row 
        if (!row["__disabled"]){ return false; }

        //If there is a row indication, then return that value
        return row["__disabled"];
    };

    //==================================
    // IsRowOrGridDisabled
    //==================================
    // Returns true also if the whole grid is disabled
    IsRowOrGridDisabled(row){

        //Return true if the grid is disabled
        if (this.disabled){ return true; }

        //Otherwise check the row
        return this.IsRowDisabled(row);
    };

    //==================================
    // MarkAsLoading
    //==================================
    //Add this so I could get concret start and stop markers for when a grid is in a loading state
    //This allows me to trigger 508 alerts directly, since it's a little difficult to get them to work
    //right just using Angular
    MarkAsLoading(bIsLoading){
        
        this.settings.loading = bIsLoading;

        //508
        window.setTimeout(()=>{

            if (bIsLoading){
                let el = document.getElementById("__cas-grid_accLiveAssertive");
                if (el){ el.innerText = "Grid data loading"; }
            }
            else{
                let el = document.getElementById("__cas-grid_accLiveAssertive");
                if (el){ el.innerText = "Grid data loading complete"; }
            }
            
        },0);
    };

    //==================================
    // MoveFocusDown
    //==================================
    MoveFocusDown() {

        //Jump out if this is not a grid cell
        var elCell = this.GetSelectedCell();
        if (!elCell.elCell) { return; }

        if (elCell.rowIndex < this.viewRows.length) {
            this.SetFocusToCell(elCell.rowIndex+1, elCell.colIndex);
        }
    }

    //==================================
    // MoveFocusLeft
    //==================================
    MoveFocusLeft() {

        //Jump out if this is not a grid cell
        var elCell = this.GetSelectedCell();
        if (!elCell.elCell) { return; }

        if (elCell.colIndex > 0) {
            this.SetFocusToCell(elCell.rowIndex, elCell.colIndex-1);
        }
    }

    //==================================
    // MoveFocusRight
    //==================================
    MoveFocusRight() {

        //Jump out if this is not a grid cell
        var elCell = this.GetSelectedCell();
        if (!elCell.elCell) { return; }

        if (elCell.colIndex < this.settings.columns.length) {
            this.SetFocusToCell(elCell.rowIndex, elCell.colIndex+1);
        }
    }

    //==================================
    // MoveFocusUp
    //==================================
    MoveFocusUp() {

        //Jump out if this is not a grid cell
        var elCell = this.GetSelectedCell();
        if (!elCell.elCell) { return; }

        if (elCell.rowIndex > 0) {
            this.SetFocusToCell(elCell.rowIndex-1, elCell.colIndex);
        }
    }

    //==================================
    // SetFocusToCell
    //==================================
    SetFocusToCell(row, col) {

        var newCellId = this.__generateGridCellId(row, col);

        //Jump to the new cell
        var elNewCell = document.getElementById(newCellId);
        if (elNewCell) { elNewCell.focus(); }
    }

    //==================================
    // SetFocusToFirstRow
    //==================================
    SetFocusToFirstRow(){

        //First, let's try to give the browser a chance to render the grid in case this
        //method was called from the same function that renders the grid
        window.setTimeout(()=>{

            //Make sure the grid is rendered
            let grid = document.getElementById(this._gridId);
            if (!grid){ 
                console.log("[cas-grid:SetFocusToFirstRow()] Could not find grid");
                return; 
            }

            //First, is there a row to select?
            if (this.settings.rows.length > 0){

                let row = grid.getElementsByClassName("__cas-grid-row")[0];
                if (row){
                    row.focus();
                    return;
                }
                
            }

            //If we're here, there were no rows to select (or they aren't rendered, as in a loading display might be up)
            //Let's try to compromise and select a button on the grid if there is one (there usually is)
            let button = grid.getElementsByClassName("__cas-grid-control-button")[0];
            if (button){
                button.focus();
            }
            
            //Nothing we can do
             console.log("[cas-grid:SetFocusToFirstRow()] Could not find row or button on grid to set focus to");


        },0);
    };

    //==================================
    // SelectRowByValue
    //==================================
    // Marks a row selected by an indicated field value
    SelectRowByValue(fieldName, value){
        
        if (this.settings.disableSelection){
            throw("[cas-grid.js:SelectRowByValue()]: disableSelection is set to true");
        }

        //Validate fieldName
        if (this.settings.columns.filter(c=>c.fieldName == fieldName).length == 0){
            throw("[cas-grid.js:SelectRowByValue()] Invalid fieldName: " + fieldName);
        }

        let rows = this.viewRows.filter(r=>r[fieldName] == value);
        if (rows.length > 0){
            this._selectRow(rows[0]);
            //this.onRowSelectionChanged.emit(); //This should be happening already in the _selectRow function
        }
    };

    //==================================
    // SelectRowByPayloadValue
    //==================================
    // Each row is associated with an object and that object may have fields
    // that aren't actually displayed on thw grid.  This method allows you to
    // select a row based on any values in that object, displayed or not.
    //
    // Returns false if indicated row is not found
    SelectRowByPayloadValue(fieldName, value){

        if (this.settings.disableSelection){
            throw("[cas-grid.js:SelectRowByValue()]: disableSelection is set to true");
        }

        let rows = this.viewRows.filter(r=>r[fieldName] == value);
        if (rows.length > 0) {
            this._selectRow(rows[0]);
            return true;
        }
        else {
            return false;
        }
    };

    //==================================
    // SetFilter
    //==================================
    // Adds or updates a filter by this name
    // 
    // name<str>:           //(req) Name for this filter
    // fnFilter<fn(row)>:   //(req) Filter to apply to rows for the grid
    // apply<bool>:         //(opt, false) When true, this filter is applied 
    //                          immediately (if you have multiple filters, it might be useful not to do so)
    //
    // ex: myGrid.SetFilter("filter_1", (row)=>{ return row.color == 'red'; });
    SetFilter(name, fnFilter, apply){
    
        if (arguments.length < 2){
            throw("[cas-grid.cs:SetFilter()] Insufficient paramters supplied.");
        }

        //Default param
        apply = apply || false;

        //Prep the filters object if needed
        if (!this._filters){ this._filters = {}; }

        //Add/Set the filter
        this._filters[name] = fnFilter;

        //Apply filter immediately?
        if (apply){
            this.ApplyFilters();
            this._changePage('first');
        }
    };

    //==================================
    // SetFilters
    //==================================
    // Adds/Sets multiple filters at once
    //
    // filters<obj>:        //(req) { name<str>:fn<fn>, ... }
    // apply<bool>:         //(opt, false) When true, this filter is applied 
    SetFilters(filters, apply){

        //Default param
        apply = apply || false;

        //Prep the filters object if needed
        if (!this._filters){ this._filters = {}; }

        //Grab the filter names
        let arrNames = Object.keys(filters);

        //Apply the filters
        //let name = arrNames[0];
        //this._filters[name] = this.filters[name];
        arrNames.map((name)=>{ this._filters[name] = filters[name]; });

        //Apply filter immediately?
        if (apply){
            this.ApplyFilters();
            this._changePage('first');
        }
    }

    //==================================
    // SetPage
    //==================================
    SetPage(newPage) {
        // Note: I added this much later so I'm not sure if there's any
        // risks to simply exposing it.  Keep an eye on it to see if any 
        // conditions need to be addressed.
        //
        // Returns false if the page could not be set (probably out of range)
        var success = this._setCurrentPage(newPage);
        if (!success) {
            //Might have been out of range (or disabled).  Try setting it to the first page so any callers get some sort of valid page..
            this.GoToFirstPage();
            return false;
        }

        return true;
    }

    //==================================
    // SetRows
    //==================================
    SetRows(data) {
        this.ClearSort();
        this.settings.rows = data;
        this.ApplyFilters();
        this._changePage('first');
    }

    //==================================
    // SetRowsAsync
    //==================================
    //Set's the rows one at a time async to give the browser time to render
    SetRowsAsync(data, fnOnComplete) {
        
        this.ClearRows();
        this.ClearSort();

        for (var i = 0; i < data.length; i++) {

            ((i) => {

                setTimeout(() => {
                    this.settings.rows.push(data[i]);

                    //Last row
                    if (i >= data.length-1) {
                        setTimeout(() => {
                            this.ApplyFilters();
                            this._changePage('first');
                            if (fnOnComplete) { fnOnComplete(); }
                        }, 10);
                    }

                }, 10);
            })(i);
            
        }
        
    }

    //==================================
    // SortGrid
    //==================================
    SortGrid(columnName, direction, fnOnComplete){

        //Make sure the grid has time to have been referenced by Angular (ug)
        setTimeout(()=>{

            //Grab the column
            let column = this.settings.columns.filter(c => c.fieldName==columnName);
            if (column.length == 0){ return; }
            column = column[0];

            //Set the direction
            direction = direction == 'A' ? 'A' :'D'

            //Set the column sort
            column.sorted = direction;
        
            this._sortColumn(column, false);    

            if (fnOnComplete) { fnOnComplete(); }
        
        }, 0)
    }; 

    //==================================
    // UnselectAllRows
    //==================================
    UnselectAllRows(){
        this.GetSelectedRows().map((r)=>{ 
            r.__selected=false;
            this.onRowUnSelected.emit(r);
        });
        this.onRowSelectionChanged.emit();
    };


    // PRIVATE FUNCTIONS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // _changePage
    //==================================
    _changePage(command){
        
        if (this.disabled){ return; }

        var newPage = 0;
        
        switch(command.toLowerCase()){
            case 'first': newPage = 1; break;
            case 'prev': newPage = +this._currentPage - 1; break;
            case 'next': newPage = +this._currentPage + 1; break;
            case 'last': newPage = this.totalPages; break;
            default: throw("[cas-grid.js:_changePage()] Invalid command"); break;
        }

        //Jump out if this is not a valid page change
        if (!this._isPageChangeValid(newPage)){
            return;
        }

        //Set the page
        this._setCurrentPage(newPage);
    };

    //==================================
    // _columnSortable
    //==================================
    _columnSortable(col){
        
        //Validate
        if (!this.settings){ throw("[cas-grid:_columnSortable()] - Cannot call _columnSortable() prior to initialization of this.settings (in ngOnInit())"); }

        return (col.sortable === undefined || col.sortable === null) ? this.settings.sortByDefault : col.sortable;
    };

    //================================== 
    // _getFirstTabableElement
    //================================== 
    _getFirstTabableElement(parentElement) {
        //console.log("_getFirstTabableElement, parentElement:", parentElement);
        return parentElement.querySelector('[tabindex]:not([tabindex="-1"])');
    }

    //================================== 
    // _getToolTip
    //================================== 
    _getToolTip(row, column){

        if (column.toolTipFn){
            var cellValue = row[column.fieldName];
            return column.toolTipFn(cellValue, row);
        }

        return "";
    }

    //================================== 
    // _getToolTipForJaws
    //================================== 
    _getToolTipForJaws(row, column) {

        if (column.toolTipFn) {
            var cellValue = row[column.fieldName];
            return column.ariaCellFn(cellValue, row);
        }

        return "";
    }

    //================================== 
    // _getTruncatedColumnHeader
    //==================================
    _getTruncatedColumnHeader(header){
        
        var s = header;

        if (s.length > this.settings.maxColHeaderLength){
            s = s.substr(0, this.settings.maxColHeaderLength-3) + "...";
        }

        return s;
    };

    //==================================
    // _isEmpty
    //==================================
    _isEmpty(val) {
        return typeof val === "undefined" || val === null;
    }

    //==================================
    // _isPageChangeValid
    //==================================
    _isPageChangeValid(newPage){
        if (isNaN(newPage) || newPage < 1 || newPage > this.totalPages || newPage == this.settings.currentPage){
            return false;
        }
        return true;
    };

    //==================================
    // _isPageValid
    //==================================
    _isPageValid(newPage){
        if (isNaN(newPage) || newPage < 1 || newPage > this.totalPages){
            return false;
        }
        return true;
    };

    //==================================
    // _preRenderCellProcessing
    //==================================
    // All cell values are passed through this method to get the final output to the screen
    _preRenderCellProcessing(column, rawValue){
        
        let newValue = typeof(rawValue) === "undefined" ? "--": rawValue;
        
        //Truncation processing (for non null strings, presuming the user doesn't configure truncate for non-strings)
        if (column.truncate && newValue !== null){
            newValue = newValue.substring(0, column.truncate);
        }

        //Empty cell processing
        if (!this.settings.allowEmptyCells){
            if (newValue === "" || newValue === null){
                newValue = this.settings.blankCellValue;
            }
        }

        return newValue;
    };

    //==================================
    // _setCurrentPage
    //==================================
    // Returns false if the page change cannot be done
    _setCurrentPage(newPage){

        if (this.disabled){ return false; }

        //Check to see if this is a valid request
        if (!this._isPageChangeValid(newPage)){
            return false;
        }

        //Set the current page value
        this._currentPage = newPage;

        //Update the page input display in case it hasn't been yet
        this._currentPageInput = newPage;

        //Fire a page changed event for the parent
        this.onPageChanged.emit(newPage);

        //Scroll to top
        let gridBody$ = $("#body_" + this._gridId);
        if (gridBody$.length > 0){
            gridBody$[0].scrollTop = 0;
        }

        return true;
    };

    //==================================
    // _setFocusToFirstTabable
    //==================================
    _setFocusToFirstTabable(parentElement) {

        //Wait a moment for Angular to render the change then select the first tabable control in the editable cell
        window.setTimeout(() => {
            var elTabable = this._getFirstTabableElement(parentElement);
            if (elTabable) { elTabable.focus(); }
        }, 10); //0 should work, but let's give our paste eating friend (IE11) an extra moment or two to catch up
    };

    //==================================
    // _selectRow
    //==================================
    _selectRow(row){
        
        if (this.disabled){ return; }

        if (this.settings.disableSelection){ return; }

        //If this is not currently a multi select grid, unselect any currently selected rows
        if (!this.settings.multiSelect){
            this.UnselectAllRows();
        }
        
        //Select the current row
        row.__selected = true;

        this.onRowSelected.emit(row);
        this.onRowSelectionChanged.emit();
    };

    //==================================
    // _sortColumn 
    //==================================
    _sortColumn(column, flip=true){

        if (this.disabled) { return; }

        let dataType = column.dataType || 'string';

        //Jump out if this column is not sortable
        //if (!column.sortable){ return; }
        if (!this._columnSortable(column)){ return; }

        //Check to see if the current column is already sorted
        let curSort = column.sorted || null;

        //Clear any sorts (only one sort column at a time for now)
        this.settings.columns.filter(c=>!!c.sorted).map((col)=>{ col.sorted = null; });

        //Mark this column with the proper sort direction
        column.sorted = flip ? this.__flipSortDirection(curSort) : curSort;               

        //Sort the data
        this.viewRows.sort((row1, row2) => {

            var value1;
            var value2;                      

            if (column.procDataFn){
                value1 = row1[column.fieldName] ? column.procDataFn(row1[column.fieldName], row1) : null;
                value2 = row2[column.fieldName] ? column.procDataFn(row2[column.fieldName], row2) : null;                

                value1 = row1[column.fieldName] ? column.procDataFn(row1[column.fieldName]) : null;
                value2 = row2[column.fieldName] ? column.procDataFn(row2[column.fieldName]) : null;             
            }
            else{
                value1 = row1[column.fieldName] ? this.__toLower(row1[column.fieldName]) : null;
                value2 = row2[column.fieldName] ? this.__toLower(row2[column.fieldName]) : null; 
            }

            if (dataType === 'date') {

                if (value1 !== null && !isNaN(Date.parse(value1)))
                    value1 = Date.parse(value1);

                if (value2 !== null && !isNaN(Date.parse(value2)))
                    value2 = Date.parse(value2);
            }          
            

            if (value1 === null || (value1 < value2)){
                return column.sorted == 'A' ? -1 : 1;
            }
            else if (value2 === null || (value1 > value2)){
                return column.sorted == 'A' ? 1 : -1;
            }

            //if ((row1[column.fieldName].toLowerCase() < row2[column.fieldName].toLowerCase()) || row1[column.fieldName] === null || row1[column.fieldName] === undefined){
            //    return column.sorted == 'A' ? -1 : 1;
            //}
            //else if ((row1[column.fieldName].toLowerCase() > row2[column.fieldName].toLowerCase()) || row2[column.fieldName] === null || row2[column.fieldName] === undefined){
            //    return column.sorted == 'A' ? 1 : -1;
            //}
            return 0;
        });
       
        //Notify any subscribers that we've sorted
        this.onSort.emit(column);
    };

    //==================================
    // _toggleRow
    //==================================
    _toggleRow(row){

        //console.log("[_toggleRow] row.__selected (before):", row.__selected);
        
        if (this.disabled){ return; }

        if (this.settings.disableSelection){ return; }

        //If this row is already selected, unselect it
        if (row.__selected){
            this._unselectRow(row);
        }
        else{
            this._selectRow(row);
        }

        //console.log("    row.__selected (after):", row.__selected);

        //508
        window.setTimeout(() => {
            let el = document.getElementById("__cas-grid_accLiveAssertive");
            if (row.__selected) {
                if (el) { el.innerText = "Row is selected"; }
            }
            else {
                if (el) { el.innerText = "Row is not selected"; }
            }
        }, 0);
    };

    //==================================
    // _unselectRow
    //==================================
    _unselectRow(row){
        
        if (this.disabled){ return; }

        if (this.settings.disableSelection){ return; }

        if (!row.__selected){ return; }//Nothing to do

        row.__selected = false;
        this.onRowUnSelected.emit(row)
        this.onRowSelectionChanged.emit();
    };


    // PRIVATE HELPERS
    //-------------------------------------------------------------------------------------------------------------------------
    ////==================================
    //// __clamp
    ////==================================
    //__clamp(val, min, max){
    //    return Math.max(min, Math.min(max, val));
    //}

    //================================================
    // __isBlank
    //================================================
    //  Returns true for values that are null, undefined, or a blank string.
    //  All other values will return a false (including 0)
	__isBlank(val) {
		return val === null || typeof(val) === "undefined" || val === "";
	};

    //==================================
    // __flipSortDirection
    //==================================
    __flipSortDirection(sort){
        sort = sort || null;
        return sort == 'A' ? 'D' : 'A';
    };

    //==================================
    // __generateGridCellId
    //==================================
    __generateGridCellId(row, col) {
        return this._gridId + '_cell_' + row + '_' + col
    }

    //==================================
    // __generateGridId
    //==================================
    __generateGridId(){
        
        //Custom grid id?
        if (this.settings.gridId) { 
            
            //Check for any duplicates
            let el = document.getElementById(this.settings.gridId);
            if (el != null) {
                throw ("[cas-grid:__generateGridId] Supplied gridId matches an element already in the DOM.  Aborting.");
            }
            this._gridId = this.settings.gridId; 
        }

        //Don't do anything if we already have a grid id
        if (this._gridId){ return; }

        //The finite loop is a safety feature
        for (let i=0; i<100; i++){

            //Generate an id
            let rand = Math.floor(Math.random() * 100000);
            let gridId = '__cas_grid_' + rand;

            //Test to make sure its not in use
            if (!document.getElementById(gridId)){
                this._gridId = gridId;
                return;
            }

            //Try again until we have no collisions (should be vanishingly rare)
        }

        throw("[cas-grid.js:__generateGridId] - Unexpected error: Unable to find an unused gridId");
    };

    //==================================
    // __getGridMinWidth
    //==================================
    __getGridMinWidth(){

        //Get the base min size from the min column widths or a small default value
        let gridMinWidth = this.settings.columns.reduce((acc, col)=>{  
            
            //Handle nulls/undefined
            var colMinWidth = col.minWidth || 15; //default to 15

            //Check for invalid inputs 
            if (isNaN(colMinWidth)){ throw("[cas-grid:ngOnInit()] - Please provide numbers for the minWidth column property (these are treated as px)"); }

            //Add a little for the standard padding (this helps make the grid wide enough that it doesn't get lost under the scroll bar)
            colMinWidth += 8;

            //Return the sum
            return (acc + colMinWidth);
        }, 0);

        //Add in the y-scroll bar (which is always displayed)
        gridMinWidth += this.__getScrollbarWidth();

        return gridMinWidth;
    };

    //==================================
    // __getScrollbarWidth
    //==================================
    __getScrollbarWidth() { 
        var div = document.createElement("div");
		div.setAttribute("style", "visibility:hidden; position:absolute; top:-200px; left:-200px; width:100px; height:100px; overflow:scroll;");
        document.body.appendChild(div);
        var sbwidth = div.offsetWidth - div.clientWidth;
        document.body.removeChild(div);
		return sbwidth;
    };

    //==================================
    // __toLower
    //==================================
    // Just a shortcut for applying toLower to anything cleanly
    __toLower(val) {
        return val.toLowerCase ? val.toLowerCase() : val;
    }
}
    ////==================================
    //// __findGridcellParent
    ////==================================
    //// Searches up the dom tree to find the "closest" parent that is a grid cell
    //// Returns the parent gridcell or null if not found.
    //__findGridcellParent(el) {
        
    //    if (el === document.body) { return null; } //Just in case
    //    if (el.getAttribute("role") == "gridcell") { return el; }

    //    return this.__findGridcellParent(el.parentElement);
    //}